/**
 * In-place sign-in recovery — CSRF + query-error copy.
 *
 * After session expiry the login form must become submittable without a
 * full document reload. Logout must not race this token away.
 *
 * Concurrent GET /api/auth/csrf calls are single-flighted. A cancelled
 * React effect must not mint a second cookie while the form keeps the
 * first header token (that mismatch is "Sign-in expired").
 */

export const CSRF_UNAVAILABLE =
  'Sign-in could not be prepared. Try again — you do not need to reload the page.'

/** Query `error=` values that mean "stay on login and re-authenticate". */
export const LOGIN_REAUTH_ERRORS = new Set([
  'session_expired',
  'session_required',
  'verification_failed',
  'invalid_user_type',
  'invalid_state',
  'session_contamination',
  'employee_session_not_allowed',
  'client_portal_only',
])

export function isLoginReauthError(code: string | null | undefined): boolean {
  if (!code) return false
  return LOGIN_REAUTH_ERRORS.has(code)
}

export function loginQueryErrorMessage(code: string | null | undefined): string | null {
  switch (code) {
    case 'sso_unavailable':
      return 'Sign-in is temporarily unavailable. Please try again shortly.'
    case 'callback_failed':
      return 'Authentication failed. Please try again.'
    case 'invalid_state':
    case 'session_expired':
    case 'session_required':
    case 'verification_failed':
      return 'Your session ended. Sign in to continue.'
    case 'session_contamination':
    case 'invalid_user_type':
      return 'Session conflict detected. Please sign in again.'
    case 'employee_session_not_allowed':
    case 'client_portal_only':
      return 'This portal is for client accounts. Sign in with your client credentials.'
    default:
      return null
  }
}

export type FetchCsrfOptions = {
  attempts?: number
  /** Delay before each attempt, including the first (usually 0). */
  delaysMs?: number[]
  /** Hard stop for the whole retry loop (ms). */
  deadlineMs?: number
  fetchFn?: typeof fetch
  sleep?: (ms: number) => Promise<void>
  /** Mint a new cookie even if one already exists (403 recovery). */
  rotate?: boolean
}

async function defaultSleep(ms: number): Promise<void> {
  if (ms <= 0) return
  await new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

let csrfInflight: Promise<string | null> | null = null

/**
 * Drop a stuck single-flight promise so a remounted login page can mint again.
 * Safe to call from the login bootstrap effect (Strict Mode / soft nav).
 */
export function resetCsrfTokenInflight(): void {
  csrfInflight = null
}

function browserFetch(): typeof fetch {
  if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
    return window.fetch.bind(window)
  }
  return fetch
}

async function fetchCsrfTokenOnce(
  fetchFn: typeof fetch,
  rotate: boolean
): Promise<string | null> {
  const url = rotate ? '/api/auth/csrf?rotate=1' : '/api/auth/csrf'
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 8000)
  try {
    const response = await fetchFn(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      signal: controller.signal,
      ...(rotate ? { headers: { 'x-csrf-rotate': '1' } } : {}),
    })
    if (!response.ok) return null
    const data = (await response.json()) as { csrfToken?: string }
    return typeof data.csrfToken === 'string' && data.csrfToken ? data.csrfToken : null
  } finally {
    clearTimeout(timer)
  }
}

export async function fetchCsrfToken(
  fetchFn?: typeof fetch,
  options: { rotate?: boolean } = {}
): Promise<string | null> {
  const doFetch = fetchFn ?? browserFetch()
  const rotate = Boolean(options.rotate)
  if (csrfInflight && !rotate) return csrfInflight

  const previous = csrfInflight
  const pending = (async () => {
    if (rotate && previous) {
      try {
        await previous
      } catch {
        /* rotate after the in-flight mint settles */
      }
    }
    return fetchCsrfTokenOnce(doFetch, rotate)
  })()

  csrfInflight = pending
  void pending
    .finally(() => {
      if (csrfInflight === pending) csrfInflight = null
    })
    .catch(() => {
      /* caller handles the rejection */
    })
  return pending
}

/**
 * Retry CSRF minting so a late logout Set-Cookie or cold cookie jar
 * cannot leave the form stuck on "Refresh the page".
 */
export async function fetchCsrfTokenWithRetry(
  options: FetchCsrfOptions = {}
): Promise<string | null> {
  const attempts = options.attempts ?? 4
  const delaysMs = options.delaysMs ?? [0, 200, 500, 1000]
  const deadlineMs = options.deadlineMs ?? 10_000
  const fetchFn = options.fetchFn ?? browserFetch()
  const sleep = options.sleep ?? defaultSleep
  const deadline = Date.now() + deadlineMs

  for (let i = 0; i < attempts; i += 1) {
    if (Date.now() >= deadline) break
    const delay = delaysMs[i] ?? delaysMs[delaysMs.length - 1] ?? 0
    await sleep(delay)
    try {
      // After a failed attempt, drop inflight so the next try is not pinned
      // to a hung/aborted shared promise from the previous lap.
      if (i > 0) resetCsrfTokenInflight()
      const token = await fetchCsrfToken(fetchFn, { rotate: options.rotate })
      if (token) return token
    } catch {
      /* network / abort — try again */
    }
  }
  return null
}
